import React from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { AgentDto } from '@shared/services/apiService/apiService';
import type { CampaignFileAgentScope } from '@features/Admin/KnowledgeBase/model/types';

import { useStyles } from '../KnowledgeBaseTab.styles';

interface KnowledgeBaseAssignmentControlsProps {
  agents: AgentDto[];
  agentScope: CampaignFileAgentScope;
  selectedAgentIds: string[];
  disabled: boolean;
  onScopeChange: (agentScope: CampaignFileAgentScope) => void;
  onChange: (agentIds: string[]) => void;
  onAssignAll: () => void;
}

const KnowledgeBaseAssignmentControls: React.FC<KnowledgeBaseAssignmentControlsProps> = ({
  agents,
  agentScope,
  selectedAgentIds,
  disabled,
  onScopeChange,
  onChange,
  onAssignAll,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const selectedAgents = agents.filter((agent) => selectedAgentIds.includes(agent.id));
  const areAllAgentsSelected =
    agents.length > 0 && agents.every((agent) => selectedAgentIds.includes(agent.id));

  return (
    <Stack className={classes.assignmentSection}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={agentScope}
        disabled={disabled}
        aria-label={t('campaignPage.knowledgeBase.assignments.scopeLabel')}
        onChange={(_event, value: CampaignFileAgentScope | null) => {
          if (value) onScopeChange(value);
        }}>
        <ToggleButton value="all">
          {t('campaignPage.knowledgeBase.assignments.allAgents')}
        </ToggleButton>
        <ToggleButton value="selected">
          {t('campaignPage.knowledgeBase.assignments.selectedAgents')}
        </ToggleButton>
      </ToggleButtonGroup>
      {agentScope === 'selected' ? (
        <>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disableClearable
            options={agents}
            value={selectedAgents}
            onChange={(_event, value) => onChange(value.map((agent) => agent.id))}
            getOptionLabel={(agent) => agent.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={disabled}
            renderTags={(value, getTagProps) => (
              <Stack direction="row" flexWrap="wrap" gap={0.25}>
                {value.map((agent, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={agent.id}
                    label={agent.name}
                    size="small"
                    className={classes.agentChip}
                  />
                ))}
              </Stack>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('campaignPage.knowledgeBase.assignments.label')}
                placeholder={
                  selectedAgents.length === 0
                    ? t('campaignPage.knowledgeBase.assignments.placeholder')
                    : undefined
                }
              />
            )}
          />
          <Stack direction="row" className={classes.assignmentActions}>
            <Button
              size="small"
              variant="outlined"
              onClick={onAssignAll}
              disabled={disabled || agents.length === 0 || areAllAgentsSelected}
              className={classes.assignmentButton}>
              {t('campaignPage.knowledgeBase.assignments.selectCurrentAgents')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onChange([])}
              disabled={disabled || selectedAgentIds.length === 0}
              className={classes.assignmentButton}>
              {t('campaignPage.knowledgeBase.assignments.clearSelectedAgents')}
            </Button>
          </Stack>
          {selectedAgentIds.length === 0 && (
            <Typography variant="body2" color="text.secondary" role="status">
              {t('campaignPage.knowledgeBase.assignments.noAgentAccess')}
            </Typography>
          )}
          {selectedAgentIds.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('campaignPage.knowledgeBase.assignments.selectedAgentsHint')}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" role="status">
          {t('campaignPage.knowledgeBase.assignments.allAgentsHint')}
        </Typography>
      )}
    </Stack>
  );
};

export default KnowledgeBaseAssignmentControls;
